import { Box, BoxProps, styled } from '@mui/material';
import classNames from 'classnames';
import { forwardRef, useEffect, useState } from 'react';
import WebFont from 'webfontloader';
import { resolveLucideIcon } from './lucide-icons';

export type IconLibrary = 'material-symbols' | 'lucide';

const LUCIDE_DEFAULT_SIZE = 18;
const LUCIDE_DEFAULT_STROKE_WIDTH = 1.75;

/*
  Google Material Symbols font by default will show a text-form fallback with
  the name of the icon if the font fails to load or takes too long to load.
  To prevent this, we use a loading animation to hide the text-form fallback
  and `webfontloader` library to detect when the font has finished loading.

  The `timeout` setting is the time before a font load is considered failed.
  It is set to 1 hour to prevent the font from ever timing out, otherwise
  user will see text-based fallback.
*/
const FONT_FAMILY_NAME = 'Material Symbols Outlined';
let GOOGLE_SYMBOLS_FONT_LOADED = false;
WebFont.load({
  custom: {
    families: [FONT_FAMILY_NAME],
    urls: [
      'https://fonts.googleapis.com/css2?display=block&family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200',
    ],
  },
  classes: false,
  fontinactive: (familyName) => {
    if (familyName === FONT_FAMILY_NAME) {
      GOOGLE_SYMBOLS_FONT_LOADED = true;
    }
  },
  fontactive: (familyName) => {
    if (familyName === FONT_FAMILY_NAME) {
      GOOGLE_SYMBOLS_FONT_LOADED = true;
    }
  },
  timeout: 1000 * 60 * 60, // 1 hour
});

const Container = styled(Box, { name: 'DynamicIconContainer' })<{
  weight: number;
  fill: number;
}>(({ weight, fill }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  // Setting default width and height to icon prevents layout shift when icon is loaded.
  width: '1.3125rem',
  height: '1.3125rem',
  fontSize: '1.3125rem',

  fontWeight: 300,
  // https://developer.mozilla.org/en-US/docs/Web/CSS/font-variation-settings
  // Styling syntax for variable fonts.
  fontVariationSettings: `"FILL" ${fill}, "GRAD" 0, "wght" ${weight}`,

  '@keyframes loading-shine': {
    '50%': {},
    to: {
      backgroundPosition: 0,
    },
  },

  '&.loading': {
    background:
      'linear-gradient(120deg,transparent 20%, color-mix(in srgb,gray 20%,transparent) 40%, color-mix(in srgb,gray 20%,transparent) 60%,transparent 80%) 100%/800% 100%',
    animation: 'loading-shine 2s ease infinite',
    color: 'transparent',
    fontSize: 0,
  },
}));

// Renders an icon by `name`: a Material Symbols glyph by default, or a lucide
// icon (by its lucide name) when `library="lucide"`. An unresolved lucide name
// falls back to the Material Symbols font so no icon silently disappears.
type DynamicIconProps = {
  name: string;
  library?: IconLibrary;
  filled?: boolean;
  weight?: number;
  /** lucide only */
  size?: number;
  /** lucide only */
  strokeWidth?: number;
} & BoxProps;
export const DynamicIcon = forwardRef(
  (
    {
      name,
      library = 'material-symbols',
      filled = false,
      weight = 300,
      size = LUCIDE_DEFAULT_SIZE,
      strokeWidth = LUCIDE_DEFAULT_STROKE_WIDTH,
      className,
      sx,
      ...rest
    }: DynamicIconProps,
    ref,
  ) => {
    const [isFontLoaded, setIsFontLoaded] = useState(GOOGLE_SYMBOLS_FONT_LOADED);
    useEffect(() => {
      if (isFontLoaded) return;

      const interval = setInterval(() => {
        if (GOOGLE_SYMBOLS_FONT_LOADED) {
          setIsFontLoaded(true);
          clearInterval(interval);
        }
      }, 200);
    }, [isFontLoaded]);

    const LucideIconComponent =
      library === 'lucide' ? resolveLucideIcon(name) : undefined;

    if (LucideIconComponent) {
      return (
        <Box
          ref={ref}
          component="span"
          className={className}
          sx={[
            { display: 'inline-flex', alignItems: 'center', flexShrink: 0 },
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- MUI sx-merge pattern
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
          {...rest}
        >
          <LucideIconComponent size={size} strokeWidth={strokeWidth} />
        </Box>
      );
    }

    return (
      <Container
        ref={ref}
        className={classNames('material-symbols-outlined', className, {
          loading: !isFontLoaded,
          filled,
          wght: weight,
        })}
        fill={filled ? 1 : 0}
        weight={weight}
        sx={sx}
        {...rest}
      >
        {name}
      </Container>
    );
  },
);
DynamicIcon.displayName = 'DynamicIcon';
